import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Badge, Card, CardHeader, Stat, Table, TableWrap, Td, Th } from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import type { AccountCheck, CardOpenState } from '../testrun-types';
import type { AccountCredentials } from './api';
import { useAccountFormat } from './format';
import { Notice, ReadinessIcon, ResultBoundary } from './shared';
import { StandActions } from './StandActions';

/**
 * What the stand said about an account: products, «fits for» and which stand
 * endpoints answered. The same view on the check tab and on an account's page.
 */

const CARD_BADGE: Record<CardOpenState, string> = {
  open: 'bg-pass-soft text-pass',
  pending: 'bg-block-soft text-block',
  closing: 'bg-block-soft text-block',
  blocked: 'bg-fail-soft text-fail',
  closed: 'bg-skip-soft text-skip',
  unknown: 'bg-skip-soft text-skip',
};

const Section = ({
  title,
  empty,
  children,
}: {
  title: string;
  empty?: string | null;
  children: ReactNode;
}) => (
  <Card>
    <CardHeader title={title} />
    <div className="px-4 py-3">
      {empty ? <p className="text-[13px] text-ink-muted">{empty}</p> : children}
    </div>
  </Card>
);

const Fact = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-3 text-[13px]">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right text-ink">{value}</span>
  </div>
);

/** Which stand endpoints answered and how: they explain why a section is empty. */
const Endpoints = ({ list }: { list: AccountCheck['endpoints'] }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const failed = list.filter((e) => e.status !== 200).length;
  return (
    <Card>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className="text-[14px] font-semibold text-ink">
          {t('automation.accounts.result.endpoints')}
        </span>
        <span className="text-[13px] text-ink-muted">
          {t('automation.accounts.result.requests', { count: list.length })}
          {', '}
          {failed
            ? t('automation.accounts.result.not200', { count: failed })
            : t('automation.accounts.result.all200')}
        </span>
      </button>
      {open ? (
        <TableWrap className="border-t border-border">
          <Table>
            <tbody>
              {list.map((e, i) => (
                <tr key={i}>
                  <Td className="font-mono text-[12px]">{e.path}</Td>
                  <Td className="w-24">
                    <Badge
                      className={
                        e.status === 200 ? 'bg-pass-soft text-pass' : 'bg-fail-soft text-fail'
                      }
                      title={e.error}
                    >
                      {e.status || t('automation.accounts.result.error')}
                    </Badge>
                  </Td>
                  <Td className="w-24 text-[12px] text-ink-muted">
                    {e.ms != null ? t('automation.accounts.ms', { value: e.ms }) : ''}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      ) : null}
    </Card>
  );
};

const ResultView = ({
  data,
  creds,
  canAct,
  onRefresh,
}: {
  data: AccountCheck;
  creds: AccountCredentials;
  canAct: boolean;
  onRefresh?: () => void;
}) => {
  const { t } = useT();
  const { dateTime } = useFormat();
  const { money, count, seconds, day } = useAccountFormat();
  const activeLoans = data.loans ? data.loans.filter((l) => l.active).length : null;
  const notLoaded = (list: unknown[] | null, what: 'accounts' | 'loans' | 'cards' | 'deposits') =>
    list === null
      ? t(`automation.accounts.result.${what}NotLoaded`)
      : list.length
        ? null
        : t(`automation.accounts.result.${what}None`);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-accent-soft text-accent-fg">{data.account.type}</Badge>
              <span className="text-[16px] font-semibold text-ink">
                {data.account.companyName ?? t('automation.accounts.result.noCompany')}
              </span>
            </div>
            {data.account.person || data.account.role ? (
              <p className="text-[13px] text-ink-muted">
                {[data.account.person, data.account.role].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <p className="font-mono text-[12px] text-ink-subtle">
              {data.account.phone} · {t('automation.accounts.iin')} {data.account.iin}
              {data.account.bin ? ` · ${t('automation.accounts.bin')} ${data.account.bin}` : ''}
            </p>
          </div>
          <span className="text-[12px] text-ink-subtle">
            {data.login.how}, {seconds(data.login.ms)} · {dateTime(data.checkedAt)}
          </span>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label={t('automation.accounts.result.accounts')}
          value={count(data.accounts?.length)}
        />
        <Stat
          label={
            data.cards
              ? t('automation.accounts.result.cardsOpen', {
                  open: data.cards.filter((c) => c.open === 'open').length,
                })
              : t('automation.accounts.result.cards')
          }
          value={count(data.cards?.length)}
        />
        <Stat
          label={t('automation.accounts.result.deposits')}
          value={count(data.deposits?.length)}
        />
        <Stat
          label={t('automation.accounts.result.loans')}
          value={count(activeLoans)}
          valueClassName={data.loans?.some((l) => l.overdue) ? 'text-block' : undefined}
        />
        <Stat label={t('automation.accounts.result.employees')} value={count(data.employees)} />
        <Stat label={t('automation.accounts.result.toSign')} value={count(data.paymentsToSign)} />
      </div>

      {canAct ? <StandActions data={data} creds={creds} onRefresh={onRefresh} /> : null}

      <Section title={t('automation.accounts.result.fitsFor')}>
        <ul className="space-y-2">
          {data.readiness.map((r) => (
            <li key={r.title} className="flex items-start gap-3">
              <ReadinessIcon
                status={r.status}
                label={t(`automation.accounts.readiness.${r.status}`)}
                className="mt-0.5"
              />
              <div>
                <p className="text-[13px] font-medium text-ink">{r.title}</p>
                <p className="text-[13px] text-ink-muted">{r.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        title={t('automation.accounts.result.accounts')}
        empty={notLoaded(data.accounts, 'accounts')}
      >
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>{t('automation.accounts.result.account')}</Th>
                <Th>{t('automation.accounts.result.number')}</Th>
                <Th className="text-right">{t('automation.accounts.result.balance')}</Th>
                <Th>{t('automation.accounts.result.marks')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.accounts?.map((a, i) => (
                <tr key={a.id ?? i}>
                  <Td>{a.name}</Td>
                  <Td className="font-mono text-ink-muted">{a.number ?? t('common.empty')}</Td>
                  <Td className="text-right font-mono">{money(a.balance, a.currency ?? 'KZT')}</Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {a.main ? (
                        <Badge className="bg-accent-soft text-accent-fg">
                          {t('automation.accounts.result.main')}
                        </Badge>
                      ) : null}
                      {a.special ? (
                        <Badge className="bg-block-soft text-block">
                          {t('automation.accounts.result.special')}
                        </Badge>
                      ) : null}
                      {a.blocked ? (
                        <Badge className="bg-fail-soft text-fail">
                          {t('automation.accounts.result.blocked')}
                        </Badge>
                      ) : null}
                      {a.status && a.status !== 'OPENED' ? <Badge>{a.status}</Badge> : null}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title={t('automation.accounts.result.loans')}
          empty={notLoaded(data.loans, 'loans')}
        >
          <ul className="space-y-3">
            {data.loans?.map((l, i) => (
              <li key={i} className="space-y-1">
                <div className="flex justify-between gap-2 text-[13px]">
                  <span className="text-ink">{l.type}</span>
                  {!l.active ? (
                    <Badge>{l.state ?? t('automation.accounts.result.loanClosed')}</Badge>
                  ) : l.overdue ? (
                    <Badge className="bg-fail-soft text-fail">
                      {t('automation.accounts.result.overdue')}
                    </Badge>
                  ) : (
                    <Badge className="bg-pass-soft text-pass">
                      {l.state ?? t('automation.accounts.result.loanActive')}
                    </Badge>
                  )}
                </div>
                <p className="text-[12px] text-ink-muted">
                  {[
                    l.debt != null
                      ? t('automation.accounts.result.debt', { amount: money(l.debt) })
                      : null,
                    l.nextPayment
                      ? `${t('automation.accounts.result.nextPayment', { amount: money(l.nextPayment.amount) })}${
                          l.nextPayment.date
                            ? ` ${t('automation.accounts.result.onDay', { day: day(l.nextPayment.date) ?? '' })}`
                            : ''
                        }${l.nextPayment.status ? ` (${l.nextPayment.status})` : ''}`
                      : null,
                    l.progress?.total
                      ? t('automation.accounts.result.paid', {
                          paid: l.progress.paid ?? 0,
                          total: l.progress.total,
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title={t('automation.accounts.result.cards')}
          empty={notLoaded(data.cards, 'cards')}
        >
          <ul className="space-y-2">
            {data.cards?.map((c, i) => (
              <li key={i} className="space-y-0.5">
                <div className="flex flex-wrap justify-between gap-2 text-[13px]">
                  <span className="text-ink">
                    {c.name}{' '}
                    <span className="font-mono text-[12px] text-ink-subtle">{c.number ?? ''}</span>
                  </span>
                  <span className="flex gap-1.5">
                    <Badge>{c.kind}</Badge>
                    <Badge className={CARD_BADGE[c.open]}>{c.openLabel}</Badge>
                  </span>
                </div>
                {c.status ? <p className="text-[12px] text-ink-muted">{c.status}</p> : null}
              </li>
            ))}
          </ul>
        </Section>

        <Section
          title={t('automation.accounts.result.deposits')}
          empty={notLoaded(data.deposits, 'deposits')}
        >
          <ul className="space-y-2">
            {data.deposits?.map((d, i) => (
              <li key={i} className="flex justify-between gap-2 text-[13px]">
                <span className="text-ink">
                  {d.name}{' '}
                  <span className="font-mono text-[12px] text-ink-subtle">{d.number ?? ''}</span>
                </span>
                <span className="font-mono">{money(d.balance, d.currency ?? 'KZT')}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title={t('automation.accounts.result.forAutotests')}>
          <div className="space-y-2">
            <Fact
              label={t('automation.accounts.result.counterparties')}
              value={data.counterparties ? String(data.counterparties.count) : t('common.empty')}
            />
            <Fact
              label={t('automation.accounts.result.aiOnboarding')}
              value={
                data.ai.onboardingDone == null
                  ? t('common.empty')
                  : data.ai.onboardingDone
                    ? t('automation.accounts.result.done')
                    : t('automation.accounts.result.notDone')
              }
            />
            <Fact
              label={t('automation.accounts.result.aiLimits')}
              value={
                data.ai.limits == null
                  ? t('common.empty')
                  : data.ai.limits.length
                    ? data.ai.limits
                        .map((l) => `${l.type.replace('banking.', '')}: ${money(l.total)}`)
                        .join(', ')
                    : t('automation.accounts.result.notSet')
              }
            />
          </div>
        </Section>
      </div>

      <Endpoints list={data.endpoints} />
    </div>
  );
};

/** A check result; if the stand gave something in an unexpected shape, the page says so. */
export const CheckResult = (props: {
  data: AccountCheck;
  creds: AccountCredentials;
  canAct: boolean;
  onRefresh?: () => void;
}) => {
  const { t } = useT();
  return (
    <ResultBoundary
      key={props.data.checkedAt}
      fallback={(message) => (
        <Notice tone="fail" title={t('automation.accounts.result.cannotShow')}>
          {t('automation.accounts.result.cannotShowHint', { message })}
        </Notice>
      )}
    >
      <ResultView {...props} />
    </ResultBoundary>
  );
};
